import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { pb, getCurrentUser } from "../services/pocketbase";

const GameInterest = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // Section 1 (from PocketBase)
    username: user?.username || "",
    discord_id: user?.discord_id || "",

    // Section 2 - TOG Supported Games
    supported_games: [],

    // Section 3 - Popular Titles
    popular_games: [],
    other_games: "",

    // Section 4 - Games to Support & Feedback
    games_to_support: "any",
    other_games_to_support: "",
    feedback: "",
  });

  // Validation state for each section
  const [sectionValid, setSectionValid] = useState({
    0: true, // Section 1 is always valid as it's pre-filled
    1: false,
    2: false,
    3: false,
  });

  // Steps for the stepper
  const steps = [
    "User Information",
    "TOG Supported Games",
    "Popular Titles",
    "Games to Support & Feedback",
  ];

  // TOG Supported Games list
  const togSupportedGames = [
    "Call of Duty (Any Titles)",
    "Grand Theft Auto (GTA V/Online)",
    "Minecraft",
    "Escape from Tarkov",
    "Valorant",
    "Rocket League",
    "Chess",
    "Forza Horizon/Motorsport",
  ];

  // Popular Titles list
  const popularTitles = [
    "Apex Legends",
    "Rainbow 6 Siege",
    "Fortnite",
    "Destiny 2",
    "League of Legends",
    "Counter-Strike 2",
    "Overwatch (1 and/or 2)",
    "BattleBit Remastered",
    "The Finals",
    "PUBG",
    "Marvel Rivals",
    "MTG: Arena",
    "Assetto Corsa (and/or EVO)",
    "Halo Infinite",
    "Elden Ring",
    "Starfield",
    "Palworld",
    "Warframe",
    "Payday 3",
    "Sea of Thieves",
    "DayZ",
    "Elder Scrolls Online",
  ];

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!pb.authStore.isValid) {
      navigate("/login");
    }
  }, [navigate]);

  // Validate section 2
  useEffect(() => {
    const isValid = formData.supported_games.length > 0;
    setSectionValid((prev) => ({ ...prev, 1: isValid }));
  }, [formData.supported_games]);

  // Validate section 3
  useEffect(() => {
    const isValid =
      formData.popular_games.length > 0 || formData.other_games.trim() !== "";
    setSectionValid((prev) => ({ ...prev, 2: isValid }));
  }, [formData.popular_games, formData.other_games]);

  // Validate section 4
  useEffect(() => {
    let isValid = false;

    if (formData.games_to_support === "any") {
      isValid = true; // Changed from requiring feedback
    } else if (formData.games_to_support === "other") {
      isValid = formData.other_games_to_support.trim() !== ""; // Only require the "other" field
    }

    setSectionValid((prev) => ({ ...prev, 3: isValid }));
  }, [
    formData.games_to_support,
    formData.other_games_to_support,
    formData.feedback,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e, section) => {
    const { name, checked } = e.target;

    if (checked) {
      setFormData((prev) => ({
        ...prev,
        [section]: [...prev[section], name],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [section]: prev[section].filter((game) => game !== name),
      }));
    }
  };

  const handleRadioChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      games_to_support: value,
    }));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setError("");
    setLoading(true);

    try {
      // Prepare data for submission
      const webhookData = {
        content: "New Staff Application Submission!",
        embeds: [
          {
            title: "Game Interest Form Submission",
            color: 0x00ff00,
            fields: [
              {
                name: "User Information",
                value: `**Username:** ${formData.username}\n**Discord ID:** ${formData.discord_id}`,
                inline: false,
              },
              {
                name: "TOG Supported Games",
                value:
                  formData.supported_games.length > 0
                    ? formData.supported_games.join(", ")
                    : "None selected",
                inline: false,
              },
              {
                name: "Popular Titles",
                value: `${
                  formData.popular_games.length > 0
                    ? formData.popular_games.join(", ")
                    : "None selected"
                }${
                  formData.other_games
                    ? `\n**Other Games:** ${formData.other_games}`
                    : ""
                }`,
                inline: false,
              },
              {
                name: "Games to Support",
                value:
                  formData.games_to_support === "any"
                    ? "Any of the above"
                    : `Other: ${formData.other_games_to_support}`,
                inline: false,
              },
              {
                name: "Feedback",
                value: formData.feedback || "No feedback provided",
                inline: false,
              },
            ],
            timestamp: new Date().toISOString(),
          },
        ],
      };

      const response = await fetch("/api/webhooks/game-interest", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      if (!response.ok) {
        throw new Error(`Failed to send game interest.`);
      }

      setSuccess(true);
      setLoading(false);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(err.message || "Failed to submit form");
      setLoading(false);
    }
  };

  // Render different sections based on active step
  const renderSection = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              disabled
              margin="normal"
            />
            <TextField
              fullWidth
              label="Discord ID"
              value={formData.discord_id}
              disabled
              margin="normal"
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              TOG Supported Games
            </Typography>
            <Typography paragraph>
              These are the ones we already focus on, so don't pretend you play
              them if you don't.
            </Typography>
            <FormGroup>
              {togSupportedGames.map((game) => (
                <FormControlLabel
                  key={game}
                  control={
                    <Checkbox
                      checked={formData.supported_games.includes(game)}
                      onChange={(e) =>
                        handleCheckboxChange(e, "supported_games")
                      }
                      name={game}
                    />
                  }
                  label={game}
                />
              ))}
            </FormGroup>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Popular Titles
            </Typography>
            <Typography paragraph>
              Because some of you refuse to play what we actually support.
            </Typography>
            <FormGroup>
              {popularTitles.map((game) => (
                <FormControlLabel
                  key={game}
                  control={
                    <Checkbox
                      checked={formData.popular_games.includes(game)}
                      onChange={(e) => handleCheckboxChange(e, "popular_games")}
                      name={game}
                    />
                  }
                  label={game}
                />
              ))}
            </FormGroup>
            <TextField
              fullWidth
              label="Other Games"
              name="other_games"
              value={formData.other_games}
              onChange={handleInputChange}
              margin="normal"
              placeholder="Specify other games you play"
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              What games would you like to see TOG support?
            </Typography>
            <Typography paragraph>
              Pick what you'd actually show up for.
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                name="games_to_support"
                value={formData.games_to_support}
                onChange={handleRadioChange}
              >
                <FormControlLabel
                  value="any"
                  control={<Radio />}
                  label="Any of the above."
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label="Other:"
                />
              </RadioGroup>
            </FormControl>

            {formData.games_to_support === "other" && (
              <TextField
                fullWidth
                label="Specify other games"
                name="other_games_to_support"
                value={formData.other_games_to_support}
                onChange={handleInputChange}
                margin="normal"
                required
              />
            )}

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Got feedback?
              </Typography>
              <Typography paragraph>
                Drop your thoughts here. Want more tournaments? Less
                tournaments? Think we should ban Strato? This is your chance to
                let us know what you want to see from us!
              </Typography>
              <TextField
                fullWidth
                label="Your feedback"
                name="feedback"
                value={formData.feedback}
                onChange={handleInputChange}
                multiline
                rows={4}
                margin="normal"
              />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3, backgroundColor: "rgba(40, 44, 52, 0.99)" }}>
          <Typography variant="h4" gutterBottom>
            Game Interest Submitted
          </Typography>
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            Your game interest form has been submitted successfully!
          </Alert>
          <Button variant="contained" onClick={() => navigate("/")}>
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 4,
        mb: 4,
        borderRadius: 2,
        backgroundColor: "rgba(40, 44, 52, 0.99)",
      }}
    >
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Game Interest Form
        </Typography>

        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 4,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            "& .MuiStep-root": {
              mb: { xs: 2, sm: 0 },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderSection()}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || loading}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!sectionValid[activeStep] || loading}
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              "Submit"
            ) : (
              "Next"
            )}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default GameInterest;
